import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { TrashIcon } from '@/components/icons/trash';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { ChannelFormState } from '../types';
import { removeOpeningFromChannel } from '../utils/channel-utils';

interface ChannelRowProps {
  channel: ChannelFormState;
  openingId: string;
}

export function ChannelRow({ channel, openingId }: ChannelRowProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { calculateTotalBudget } = useBudgetType();
  const { hookForm } = useJobPostingContext();
  const { setValue, getValues } = hookForm;

  const channelKey = channel.vonqDetails?.productId ?? channel.slug;

  const channelBudget = calculateTotalBudget({
    budgetType: channel.budgetType,
    budgetPerUnit: channel.openingBudgets[openingId],
    startDate: channel.startsAt,
    endDate: channel.endAt,
  });

  const displayBudget = useMemo(() => {
    if (channel.isFree)
      return intl.formatMessage({
        defaultMessage: 'Free',
        description: 'Free budget',
      });

    if (!channelBudget) return '-';

    return formatCurrency(channelBudget);
  }, [channel.isFree, channelBudget, formatCurrency, intl]);

  const handleRemoveClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const channels = getValues('channels');
      const channelData = channels[channelKey];
      if (!channelData) return;

      const result = removeOpeningFromChannel(channelData, openingId);
      if (result === null) {
        const { [channelKey]: _dropped, ...remainingChannels } = channels;
        setValue('channels', remainingChannels);
      } else {
        setValue(`channels.${channelKey}`, result);
      }
    },
    [channelKey, openingId, getValues, setValue],
  );

  return (
    <li className="group flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1">
        <div className="w-0 shrink-0 overflow-hidden opacity-0 transition-all group-hover:w-7 group-hover:opacity-100">
          <Button
            type="button"
            variant="icon"
            size="icon"
            className="size-6"
            onClick={handleRemoveClick}
          >
            <TrashIcon />
          </Button>
        </div>
        <ChannelIcon
          channelSlug={channel.slug}
          variant="small"
          size="xs"
          logoSquareUrl={channel.vonqDetails?.logoSquareUrl}
          logoRectangleUrl={channel.vonqDetails?.logoRectangleUrl}
          channelName={channel.name}
          displayTitle={channel.displayTitle}
          titleClassName="text-fx-neutral-600 font-normal"
        />
      </div>
      {/* Channel budget */}
      <Typography variant="small1" className="text-fx-neutral-600 shrink-0">
        {displayBudget}
      </Typography>
    </li>
  );
}
